import { ElementType } from "react";

export type DimensionId =
  | "COMPANY_INFORMATION"
  | "GOVERNANCE"
  | "BUSINESS_MODEL_AND_INNOVATION"
  | "ENVIRONMENTAL"
  | "SOCIAL";

export type Dimension = {
  id: DimensionId;
  label: string;
  color: string;
  icon: ElementType;
};

export type DimensionUiConfig = {
  dimension: DimensionId;
  label: string;
  color?: string;
  icon?: ElementType;
};

export type Topic = {
  topic: string;
  name: string;
  totalQuestionsCount: number;
  completedQuestionsCount: number;
  respondedQuestionsCount: number;
  completedPercentage: number;
  respondedPercentage: number;
  scoreProportion: number;
  result: {
    score: number;
    scoreProportion: number;
    scorePercentage: number;
  } | null;
};

export type TopicWithRefreshStatus = Topic & {
  needsRefresh: boolean;
};

export type DimensionGroup = "GENERAL_INFORMATION" | "IMPACT_DIMENSIONS";

export type DimensionsSummary = {
  dimension: DimensionId;
  completedPercentage: number;
  completedQuestionsCount: number;
  completedTopicsCount: number;
  description: string;
  name: string;
  respondedPercentage: number;
  respondedQuestionsCount: number;
  topicsCount: number;
  totalQuestionsCount: number;
  topics?: Topic[];
  computable: boolean;
  group: DimensionGroup;
  order: number;
  scoreProportion: number;
  result: {
    score: number;
    scorePercentage: number;
    scoreProportion: number;
  } | null;
};

export type DimensionWithConfig = DimensionsSummary & DimensionUiConfig;

export enum ChoiceValueType {
  Text = "TEXT",
  LongText = "LONG_TEXT",
  Integer = "INTEGER",
  Decimal = "DECIMAL",
  Percentage = "PERCENTAGE",
}

export enum ChoiceType {
  Composite = "COMPOSITE",
  Fillable = "FILLABLE",
  Selectable = "SELECTABLE",
}

type BaseChoice = {
  id: string;
  label: string;
  selected: boolean;
  statement: string;
};

export type SelectableChoice = BaseChoice & {
  type: ChoiceType.Selectable;
};

export type CompositeChoice = BaseChoice & {
  type: ChoiceType.Composite;
  answerMode: AnswerMode;
  choices: Choice[];
};

export type FillableChoice = BaseChoice & {
  type: ChoiceType.Fillable;
  valueType: ChoiceValueType;
  answerValue: string;
};

export type Choice = SelectableChoice | CompositeChoice | FillableChoice;

export enum AnswerMode {
  Single = "SINGLE",
  Multiple = "MULTIPLE",
  AllRequired = "ALL_REQUIRED",
}

export type Question = {
  id: string; // deprecated
  dimension: string;
  topic: string;
  subtopic: string;
  statement: string;
  source: string;
  purpose: string;
  calculation: string;
  answerMode: AnswerMode;
  completed: boolean;
  empty: boolean;
  choices: Choice[];
};

export type QuestionnaireResultStatus = "APPROVED" | "FAILED" | "NOT_EVALUATED";

export type QuizStatus =
  | "READY"
  | "INCOMPLETE"
  | "SUBMITTED"
  | "UNDER_REVIEW"
  | "RE_REVIEW"
  | "REVIEWED"
  | "DELIVERED"
  | "ARCHIVED";
